import { useEffect } from 'react';

import { useLocale } from './locale.provider';
import { useLocationContext } from './location.provider';
import { useUserContext } from './user/user.provider';
import { DEFAULT_LOCALE, normalizeLocale, SPANISH_LOCALE } from '../i18n/locales';

/**
 * Steers the DEFAULT UI language from the resolved location waterfall
 * (override → address → IP → timezone): a PR location defaults to Spanish, US to
 * English. LocaleProvider sits above LocationProvider and cannot read location
 * itself, so this bridge renders inside both (mirrors LocaleUserSync) and pushes
 * the default down.
 *
 * It never FORCES the language — it only acts while the locale is still a
 * geography-derived default (source `detected`). Any stronger signal makes it
 * back off: a `?lang` query or stored/toggled preference (source `query` /
 * `stored`), the signed-in user's `preferredLocale` (the guard below), or a
 * Spanish device language (source `device`) — so a Spanish-language device is
 * never flipped to English just because it resolves to a US location. The
 * change is applied non-persisting so it keeps following location and
 * re-detects on the next visit.
 */
export const LocationLocaleSync = (): null => {
  const { isPuertoRico } = useLocationContext();
  const { locale, source, setLocale } = useLocale();
  const { user } = useUserContext();

  // A signed-in user's cross-device preference wins even before LocaleUserSync
  // has adopted it, so never steer the language out from under it.
  const hasServerPreference = !!normalizeLocale(user?.preferredLocale);

  useEffect(() => {
    if (source !== 'detected') return; // explicit query / stored / toggle choice
    if (hasServerPreference) return;
    const target = isPuertoRico ? SPANISH_LOCALE : DEFAULT_LOCALE;
    if (locale !== target) void setLocale(target, { persist: false });
  }, [isPuertoRico, source, locale, hasServerPreference, setLocale]);

  return null;
};
